/*
 * flashmeeting-fe
 *
 * Author: Andgel BRISSAUD
 * Copyright (c) 2026 Prodeo - All Rights Reserved
 */

/* ===== External Libraries ===== */
import { AXIOS, CallbackErrorAxios, CallbackWarningAxios, CommonGlobalService, UUID } from '@umanforge/common';
import { AxiosError, AxiosResponse } from 'axios';
import { Dispatch } from '@reduxjs/toolkit';

/* ===== API Configuration ===== */
import { API } from 'constants/api.constant';

/* ===== Services ===== */
import { ToastService } from 'services/toast.service';
import { ErrorService } from 'services/error.service';
import { ResponseService } from 'services/response.service';

/* ===== Interfaces ===== */
import {
  ChartValueApi,
  ChartValueLastDate,
  CreateChartValuePayload,
  UpdateChartValuePayload,
} from 'interfaces/chartValue.interface';
import { GlobalService } from './global.service';
import { IMPORT_MODE } from 'constants/chart.constants';
import { ChartOfSectionApi } from 'interfaces/chartOfSection.interface';

const toastService = new ToastService( 'ChartValue Service' );
const errorService = new ErrorService( toastService );
const responseService = new ResponseService( toastService );

export class ChartValueService {

  /* ===== Get the date of the last recorded value for a chart ===== */
  static getLastDate = async ( chartInfoId: UUID, token: string | null, dispatch: Dispatch ): Promise<string | null> => {
    try {
      const response: AxiosResponse<ChartValueLastDate> = await AXIOS.get(
        `${API.CHART_VALUE}/${chartInfoId}/last_date`,
        await CommonGlobalService.getHeaderRequest( token )
      );
      CommonGlobalService.saveRequestToken( response, dispatch );
      const result = responseService.handleUnexpectedResponse( response );
      return result?.date ?? null;
    } catch ( error ) {
      errorService.showCatchAxiosError( error );
      return null;
    }
  };

  /* ===== Add values for a chart ===== */
  static postValue = async ( body: CreateChartValuePayload[], token: string | null, dispatch: Dispatch ): Promise<boolean> => {
    try {
      const response: AxiosResponse = await AXIOS.post(
        API.CHART_VALUE,
        { newChartValues: body },
        await CommonGlobalService.getHeaderRequest( token )
      );
      CommonGlobalService.saveRequestToken( response, dispatch );
      if ( response.status === 202 ) {
        const apiError = response.data as { err?: number; msg?: string } | undefined;
        if ( apiError?.msg ) { toastService.showError( apiError.msg ); }
        return false;
      }
      return response.status === 200 || response.status === 201;
    } catch ( error ) {
      errorService.showCatchAxiosError( error );
      return false;
    }
  };

  /* ===== Update recorded chart values ===== */
  static putValue = async ( items: ( { id: UUID } & UpdateChartValuePayload )[], token: string | null, dispatch: Dispatch ): Promise<boolean> => {
    try {
      const response: AxiosResponse = await AXIOS.put(
        API.CHART_VALUE,
        { updatedChartValues: items },
        await CommonGlobalService.getHeaderRequest( token )
      );
      CommonGlobalService.saveRequestToken( response, dispatch );
      return response.status === 200 || response.status === 201;
    } catch ( error ) {
      errorService.showCatchAxiosError( error );
      return false;
    }
  };

  /* ===== Get all recorded values for a chart ===== */
  static getAllValues = async ( chartInfoId: UUID, token: string | null, dispatch: Dispatch ): Promise<ChartValueApi[]> => {
    try {
      const response: AxiosResponse<ChartValueApi[]> = await AXIOS.get(
        `${API.CHART_VALUE}/${chartInfoId}`,
        await CommonGlobalService.getHeaderRequest( token )
      );
      CommonGlobalService.saveRequestToken( response, dispatch );
      return responseService.handleUnexpectedResponse( response ) ?? [];
    } catch ( error ) {
      errorService.showCatchAxiosError( error );
      return [];
    }
  };

  /* ===== Delete recorded chart values ===== */
  static deleteValue = async ( chartValueIds: UUID[], token: string | null, dispatch: Dispatch ): Promise<boolean> => {
    try {
      const config = await CommonGlobalService.getHeaderRequest( token );
      const response: AxiosResponse = await AXIOS.delete(
        API.CHART_VALUE,
        { ...config, params: { chartValuesId: chartValueIds } }
      );
      CommonGlobalService.saveRequestToken( response, dispatch );
      return response.status === 200 || response.status === 201;
    } catch ( error ) {
      errorService.showCatchAxiosError( error );
      return false;
    }
  };

  /* ===== Import all values from a Excel or CSV file on a chart ===== */
  static importChartValue(
    chartInfoId: UUID, file: File, mode: typeof IMPORT_MODE[keyof typeof IMPORT_MODE],
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<{ isValid: boolean, dates?: string[] }> {
    return new Promise( async resolve => {

      const formData = new FormData();
      formData.append( 'content', file );
      if ( mode ) {
        formData.append( 'mode', mode );
      }
      const requestConfig = await CommonGlobalService.getHeaderRequest( token );
      ( requestConfig.headers ?? {} )[ 'Content-Type' ] = 'multipart/form-data';

      AXIOS.post( `${ API.CHART_VALUE }/import/${ chartInfoId }`, formData, requestConfig )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 && response.data.err !== 1096 && response.data.dates == undefined ) {
            GlobalService.onAxiosWarning( response, onWarning );
          } else if ( response.data.err !== 1096 && response.data.dates == undefined && onWarning != undefined ) {
            onWarning( response );
          }
          const isValid = response.status === 201;
          resolve( { isValid, dates: response.data.err === 1096 ? response.data.dates : undefined } );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( { isValid: false } );
        } );
    } );
  }

  /* ===== Export all recorded values for a chart on a Excel file ===== */
  static exportChartValue(
    chart: ChartOfSectionApi, token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<Blob | null> {
    return new Promise( async resolve => {
      AXIOS.get( `${ API.CHART_VALUE }/export/${ chart.id }`, { ...await CommonGlobalService.getHeaderRequest( token ), responseType: 'blob' } )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }

          const blob = new Blob( [ response.data ], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          } );

          if ( response.status != 204 ) {
            const chartName = chart.title.replaceAll( ' ', '_' ).toLocaleLowerCase();
            const url = window.URL.createObjectURL( blob );
            const a = document.createElement( 'a' );
            a.href = url;
            a.download = `${ chartName }_values.xlsx`;
            document.body.appendChild( a );
            a.click();
            document.body.removeChild( a );
          } else {
            resolve( null );
            return;
          }

          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( null );
        } );
    } );
  }


}
